<template>
  <Modal ref="modalRef" width="30rem" content-class="property-access-dialog">
    <template #body>
      <View v-if="!team" justify="center" padding="md">
        <LoadingIndicator />
      </View>

      <View v-else direction="column" gap="md" padding-block="sm">
        <IconTile :icon="tile.icon" :color="tile.color" size="small" shape="rounded" colored-icon />

        <View direction="column" gap="sm">
          <Text variant="h4">{{ title }}</Text>
          <Text variant="body-medium" color="secondary" data-testid="property-access-message">{{ message }}</Text>
        </View>

        <Card v-if="manager && state !== 'added'" variant="outline-secondary" size="small">
          <View align="center" class="manager-row">
            <Avatar :user-name="managerName" size="medium" />
            <View direction="column">
              <Text variant="label-medium">{{ managerName }}</Text>
              <Text variant="body-small" color="secondary">{{ managerRole }}</Text>
            </View>
          </View>
        </Card>
      </View>
    </template>

    <template #footer>
      <!-- Nothing to ask until the unit's team says who runs it and that the viewer doesn't -->
      <template v-if="team && state === 'request'">
        <Button variant="ghost" size="medium" @click="close">Cancel</Button>
        <Button size="medium" :loading="requestAccess.isLoading.value" @click="ask">
          {{ askLabel }}
        </Button>
      </template>
      <Button v-else-if="team" size="medium" @click="close">OK</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { formatDistanceToNowStrict } from 'date-fns';
import { computed, ref, watch } from 'vue';

import { Avatar, Button, Card, IconName, IconTile, LoadingIndicator, Modal, Text, View } from '@tallo/design-system';
import { teamMemberName } from '@tallo/investor/company';

import { useRequestPropertyAccess } from '../../mutations/property-team.mutation';
import { usePropertyTeamQuery } from '../../queries/property-team.query';
import { usePropertyAccessDialogStore } from '../../stores/property-access-dialog.store';

/**
 * What a colleague who can only view a unit or building sees when they try to change it: that they
 * can't, who can let them, and a way to ask. Mounted once; opened through the store, by a guarded
 * button or by the backend refusing a change. Asking turns it into the "requested" state in place.
 */
const store = usePropertyAccessDialogStore();
const requestAccess = useRequestPropertyAccess();
const modalRef = ref<InstanceType<typeof Modal>>();
const teamQuery = usePropertyTeamQuery(() => store.subject);
// Set by a request made from this dialog, so it shows as requested before the team is read again
const askedAt = ref<string | null>(null);

const team = computed(() => (store.subject ? teamQuery.data.value : undefined));
const manager = computed(() => team.value?.manager ?? null);
const managerName = computed(() => (manager.value ? teamMemberName(manager.value) : ''));
const firstName = computed(() => managerName.value.split(' ')[0]);
const requestedAt = computed(() => askedAt.value ?? team.value?.myAccessRequest?.createdAt ?? null);

const state = computed<'request' | 'requested' | 'added'>(() => {
  if (requestedAt.value) return 'requested';
  if (team.value?.canManage) return 'added';

  return 'request';
});

const tile = computed<{ icon: IconName; color: 'neutral' | 'orange' | 'green' }>(() => {
  if (state.value === 'requested') return { icon: 'mail-01', color: 'orange' };
  if (state.value === 'added') return { icon: 'check-circle', color: 'green' };

  return { icon: 'lock-01', color: 'neutral' };
});

const title = computed(() => {
  if (state.value === 'requested') return 'Access requested';
  if (state.value === 'added') return 'You can change this property now';

  return 'You can view this property, not change it';
});

const message = computed(() => {
  if (state.value === 'added') return 'You were added since this page loaded. Try that again.';

  if (state.value === 'requested') {
    const ago = formatDistanceToNowStrict(new Date(requestedAt.value!), { addSuffix: true });

    return manager.value
      ? `${managerName.value} was told ${ago} and can add you in one click. You'll be told when that happens.`
      : `You asked ${ago}. You'll be told when someone who runs this property adds you.`;
  }

  return manager.value
    ? 'Only the people who work on it can make changes. Its manager can add you as a collaborator.'
    : 'Only the people who work on it can make changes. An admin can add you as a collaborator.';
});

const managerRole = computed(() => {
  if (state.value !== 'requested') return 'Manager';

  const ago = formatDistanceToNowStrict(new Date(requestedAt.value!), { addSuffix: true }).replace(/ minutes?/, ' min');

  return `Manager · asked ${ago}`;
});

const askLabel = computed(() => (firstName.value ? `Ask ${firstName.value} for access` : 'Request access'));

watch(
  () => store.opened,
  () => {
    askedAt.value = null;
    // The refusal may come from a page that read the team before someone changed it
    teamQuery.refetch();
    modalRef.value?.show();
  },
);

function close() {
  modalRef.value?.close();
}

async function ask() {
  const subject = store.subject;

  if (!subject) return;

  try {
    askedAt.value = (await requestAccess.mutateAsync(subject)).createdAt;
  } catch {
    // The mutation already showed the server's message; the request stays open to try again
  }
}
</script>

<style lang="scss">
@import 'styles/mixins/layout.mixins';

// Mounted once at the app root, the dialog's portal comes before any a page opens later, so a form
// whose Save was refused would cover it. Global because Modal teleports its tree out of this scope.
.modal-container:has(> .modal-content.property-access-dialog) {
  z-index: 110;
}

// On a phone the dialog is a bottom sheet, and its two buttons split the row
@include mobile {
  .modal-content.property-access-dialog .modal-footer > * {
    flex: 1;
  }
}
</style>

<style scoped lang="scss">
// Between the spacing tokens: the avatar sits closer to the name than sm would put a separate item
.manager-row {
  gap: 0.75rem;
}
</style>
